import BarChartIcon from "@mui/icons-material/BarChart";
import { Box, Button, Divider, FormControlLabel, IconButton, Switch, Typography } from "@mui/material";
import { hasCircularRecipeIngredient } from "@notemeal/recipe-utils";
import { useNutritionFlags } from "apps/web/src/hooks/useNutritionFlags";
import { trackEvent } from "apps/web/src/reporting/reporting";
import { Dispatch, useState } from "react";
import { useHasFeature } from "shared-ui/Feature";
import { inputToNumber } from "shared-ui/utils/inputUtils";
import { FullServingAmountFragment, RecipeWithServingsFragment } from "../../../types";
import { useIsFoodFormRedesign } from "../../FoodFormUi/FoodFormUiContext";
import { ServingAmountsEditChipListWithSearchBar } from "../../ServingAmounts/Edit/ChipListWithSearchBar";
import { useSnackbar } from "../../Snackbar/SnackbarContext";
import { NewChip } from "../../universal/NewChip";
import { RecipeAssistedMethodButtons } from "./RecipeAssistedMethodButtons";
import { RecipeAssistedStartCards } from "./RecipeAssistedStartCards";
import { UnmatchedIngredientsWarning } from "./UnmatchedIngredientsWarning";
import { getRecipeAssistedEntryMode, hasRecipeAssistedEntryActions } from "./recipeAssistedEntry";
import { canSaveRecipeFormTooltips, formValidationMessages, RecipeFormAction, RecipeFormState } from "./utils";

interface IngredientsProps {
  state: RecipeFormState;
  dispatch: Dispatch<RecipeFormAction>;
  setNutrientsOpen: (value: boolean) => void;
  disabled?: boolean;
  onAiGenerateClicked: null | (() => void);
  onAiTranscribeClicked?: () => void;
  onImportFromLinkClicked?: () => void;
  forStaff?: boolean;
  openScaleRecipe?: () => void;
}

export const Ingredients = ({
  state,
  dispatch,
  setNutrientsOpen,
  disabled,
  onAiGenerateClicked,
  onAiTranscribeClicked,
  onImportFromLinkClicked,
  forStaff,
  openScaleRecipe,
}: IngredientsProps) => {
  const isRedesign = useIsFoodFormRedesign();
  const canSaveTooltips = canSaveRecipeFormTooltips(state);
  const hasRecipeScaling = useHasFeature("performanceKitchen");
  const { isGetRecipeFromUrlEnabled } = useNutritionFlags();
  const { setMessage } = useSnackbar();
  const [displayMacros, setDisplayMacros] = useState(true);

  const handleChangeIngredients = (servingAmounts: readonly FullServingAmountFragment[], recipe?: RecipeWithServingsFragment) => {
    if (hasCircularRecipeIngredient(state.id, servingAmounts, recipe)) {
      setMessage("error", "A recipe cannot be added to itself.");
      return;
    }
    dispatch({
      type: "CHANGE_INGREDIENTS",
      value: servingAmounts,
    });
  };

  const isValidIngredients = canSaveTooltips.includes(formValidationMessages.INGREDIENTS) && state.saveClicked;
  const showLink = Boolean(isGetRecipeFromUrlEnabled && onImportFromLinkClicked);
  const assistedHandlers = {
    showLink,
    onImportFromLink: showLink ? onImportFromLinkClicked : undefined,
    onTranscribePhoto: onAiTranscribeClicked,
    onGenerateWithAi: onAiGenerateClicked ?? undefined,
  };
  const hasAssistedMethods = hasRecipeAssistedEntryActions(assistedHandlers);
  const entryMode = getRecipeAssistedEntryMode(state.ingredients.length);
  const showStartCards = hasAssistedMethods && entryMode === "cards" && !disabled;
  const showCompactMethods = hasAssistedMethods && entryMode === "compact" && !disabled;

  return (
    <Box sx={{ gap: 2, display: "flex", flexDirection: "column" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h4">Ingredients</Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {isRedesign && (
            <FormControlLabel
              label="Display macros"
              control={
                <Switch disabled={disabled} size="small" checked={displayMacros} onChange={(_, checked) => setDisplayMacros(checked)} />
              }
              sx={{ m: 0, gap: 1 }}
            />
          )}
          <IconButton
            onClick={() => setNutrientsOpen(true)}
            aria-label="Open nutrient analysis"
            sx={
              isRedesign
                ? {
                    width: 36,
                    height: 36,
                    border: 1,
                    borderColor: "greyscale.300",
                    borderRadius: 1,
                    p: 0,
                  }
                : undefined
            }
          >
            <BarChartIcon sx={{ color: "accents.yellow.400" }} />
          </IconButton>
          {isRedesign && (hasRecipeScaling || forStaff) && openScaleRecipe && (
            <Button
              variant="outlined"
              disabled={disabled || state.ingredients.length === 0 || !inputToNumber(state.serving.perRecipeYieldInput)}
              onClick={() => {
                openScaleRecipe();
                trackEvent("Nutrition | Team | Recipe | Scale Recipe", {});
              }}
            >
              Scale Recipe
              <NewChip feature="scalingRecipe" />
            </Button>
          )}
        </Box>
      </Box>
      {isValidIngredients && (
        <Typography variant="body2" color="error">
          Please add at least one ingredient
        </Typography>
      )}
      <UnmatchedIngredientsWarning unmatchedIngredients={state.unmatchedIngredients} />
      {showStartCards && (
        <>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography variant="body2Semibold">Start your ingredient list</Typography>
            <Typography variant="body2" color="text.secondary">
              Bring a recipe in from somewhere else, or add ingredients one at a time. Everything stays editable.
            </Typography>
          </Box>
          <RecipeAssistedStartCards {...assistedHandlers} />
          <Divider
            sx={{
              my: 0.5,
              fontSize: 11,
              fontWeight: 500,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: "text.secondary",
              "&::before, &::after": {
                borderColor: "greyscale.300",
              },
            }}
          >
            or add ingredients yourself
          </Divider>{" "}
        </>
      )}
      <ServingAmountsEditChipListWithSearchBar
        disabled={disabled}
        enableBranded={true}
        selectedServingAmounts={state.ingredients}
        selected
        noCache
        includeRecipeIngredients
        displayMacros={displayMacros}
        onChange={handleChangeIngredients}
        sx={{ overflowY: "auto", overflowX: "hidden" }}
        toolbarSlot={showCompactMethods ? <RecipeAssistedMethodButtons {...assistedHandlers} /> : undefined}
      />
    </Box>
  );
};
